/**
 * Pending Web-Share-Target intake.
 *
 * The service worker writes shared payloads to IndexedDB
 * (`agentchat-pending-shares` / `shares`) and 303s the user back to
 * `/chats/pwa-auto-x-links?new_topic_share=<id>` for text/link shares, or
 * `/chats?share=<id>` for file shares. The React app reads that id, pulls
 * the row, and either pre-fills the X Links New Topic dialog or surfaces a
 * "tap a chat" attach banner. Once consumed, the IDB row is deleted.
 *
 * Schema mirrors what sw.js writes:
 *   { id, files: [{ blob, filename, mime_type, size_bytes }], title, text, url, created_at }
 */

const DB_NAME = 'agentchat-pending-shares'
const DB_VERSION = 1
const STORE = 'shares'

type StoredFile = {
  blob: Blob
  filename: string
  mime_type: string
  size_bytes: number
}

type StoredShare = {
  id: number
  files: StoredFile[]
  title?: string
  text?: string
  url?: string
  created_at: number
}

export type PendingShare = {
  id: number
  files: File[]
  text: string
}

function addUniqueSharePart(parts: string[], value: string | undefined): void {
  const trimmed = (value || '').trim()
  if (!trimmed) return
  if (parts.includes(trimmed)) return
  parts.push(trimmed)
}

function formatPendingShareText(row: StoredShare): string {
  const parts: string[] = []
  addUniqueSharePart(parts, row.title)
  addUniqueSharePart(parts, row.text)
  const url = (row.url || '').trim()
  if (url && !parts.some((part) => part.includes(url))) parts.push(url)
  return parts.join('\n').trim()
}

function _openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION)
    req.onupgradeneeded = () => {
      const db = req.result
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: 'id', autoIncrement: true })
      }
    }
    req.onsuccess = () => resolve(req.result)
    req.onerror = () => reject(req.error)
  })
}

async function _get(id: number): Promise<StoredShare | null> {
  const db = await _openDb()
  return await new Promise<StoredShare | null>((resolve, reject) => {
    const tx = db.transaction(STORE, 'readonly')
    const req = tx.objectStore(STORE).get(id)
    req.onsuccess = () => resolve((req.result as StoredShare | undefined) ?? null)
    req.onerror = () => reject(req.error)
  })
}

async function _delete(id: number): Promise<void> {
  const db = await _openDb()
  return await new Promise<void>((resolve, reject) => {
    const tx = db.transaction(STORE, 'readwrite')
    tx.objectStore(STORE).delete(id)
    tx.oncomplete = () => resolve()
    tx.onerror = () => reject(tx.error)
  })
}

/** Read (without deleting) a pending share. Used to surface the banner.
 *  The actual files stay in IDB until the user picks a chat. */
export async function peekPendingShare(id: number): Promise<PendingShare | null> {
  try {
    const row = await _get(id)
    if (!row) return null
    const files: File[] = (row.files || []).map((sf) => new File(
      [sf.blob], sf.filename, { type: sf.mime_type, lastModified: row.created_at },
    ))
    const text = formatPendingShareText(row)
    return { id: row.id, files, text }
  } catch (err) {
    console.warn('peekPendingShare failed:', err)
    return null
  }
}

export async function discardPendingShare(id: number): Promise<void> {
  await _delete(id).catch(() => {})
}
